import type { MenuPermissionKey } from './menu-permissions';

export interface Cliente {
  id_cliente: number;
  nome: string;
  documento?: string;
  razao_social?: string;
  cnpj?: string;
  inscricao_estadual?: string;
  email?: string;
  telefone?: string;
  contato?: string;
  cep?: string;
  endereco?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  uf?: string;
  observacao?: string;
  ativo: boolean;
  criado_em?: string;
}

export interface Fornecedor {
  id_fornecedor: number;
  nome: string;
  cnpj?: string | null;
  contato?: string | null;
  telefone?: string | null;
  email?: string | null;
  endereco?: string | null;
  observacao?: string | null;
  ativo: boolean;
  criado_em?: string;
}

export interface Usuario {
  cod_usuario: number;
  id_usuario: number;
  nome: string;
  email: string;
  login?: string;
  cpf?: string;
  cpf_cadastrado?: boolean;
  chave_pix?: string;
  percentual_pagamento: number;
  percentual_pagamento_programado?: number;
  data_inicio_percentual?: string;
  percentuais_pagamento?: PercentualPagamento[];
  tipo_usuario: 'ADMINISTRADOR' | 'EXPEDICAO';
  ativo: boolean;
  permissoes_menu?: MenuPermissionKey[];
  data_inclusao?: string;
  criado_em?: string;
}

export interface PercentualPagamento {
  id_percentual: number;
  percentual_pagamento: number;
  data_inicio: string;
}

export interface Produto {
  cod_produto: number;
  codigo_produto: string;
  descricao: string;
  valor_unitario: number;
  data_criacao?: string;
  ativo: boolean;
}

export interface ContagemItem {
  id_item?: number;
  id_produto?: number;
  codigo_produto?: string;
  produto?: string;
  tamanho: string;
  quantidade: number;
  defeitos?: number;
  total?: number;
}

export interface ContagemLog {
  id_log: number;
  id_contagem: number;
  id_usuario?: number;
  usuario?: string;
  acao: 'REABERTURA' | 'EDICAO' | string;
  status_anterior?: string;
  status_novo?: string;
  observacao?: string;
  dados?: unknown;
  criado_em: string;
}

export interface ContagemAnexo {
  id_anexo: number;
  id_contagem: number;
  tipo: 'RASCUNHO' | string;
  nome_original?: string;
  mime_type: string;
  tamanho_bytes: number;
  criado_por?: number;
  criado_por_nome?: string;
  criado_em: string;
}

export interface ContagemFotoUpload {
  nome: string;
  tipo: string;
  dados: string;
}

export interface ContagemAiProdutoDisponivel {
  id_produto: number;
  codigo_produto: string;
  produto: string;
  tamanhos: string[];
}

export interface ContagemAiUsuarioDisponivel {
  id_usuario: number;
  nome: string;
}

export interface ContagemAiItemSugestao {
  id_produto: number;
  tamanho: string;
  quantidade: number;
  defeitos: number;
  confianca?: number;
}

export interface ContagemAiResponsavelSugestao {
  id_usuario: number;
  nome: string;
  confianca?: number;
}

export interface ContagemAiSugestao {
  itens: ContagemAiItemSugestao[];
  responsaveis?: ContagemAiResponsavelSugestao[];
  observacao?: string;
}

export interface DashboardRankingResponsavel {
  cod_usuario?: number | null;
  responsavel: string;
  total_contagens: number;
  total_pecas: number;
}

export interface DashboardPecasDia {
  data: string;
  total_pecas: number;
}

export interface DashboardResponsavelReceber {
  responsavel: string;
  total_pendente: number;
  total_pix_pendente: number;
  total_receber: number;
  pagamentos_pendentes: number;
  pix_pendentes: number;
}

export interface DashboardRecebimentoDia {
  data?: string | null;
  total: number;
  total_pendente: number;
  total_pix_pendente: number;
  pagamentos: number;
}

export interface DashboardRecebimentoRealizadoDia {
  data?: string | null;
  total: number;
  pagamentos: number;
}

export interface DashboardResponsavelDetalhe {
  responsavel: string;
  total_pendente: number;
  total_pix_pendente: number;
  total_receber: number;
  total_pago: number;
  recebimentos_por_dia: DashboardRecebimentoDia[];
  recebimentos_realizados_por_dia: DashboardRecebimentoRealizadoDia[];
}

export interface DashboardResumo {
  total_contagens_dia: number;
  total_pecas_dia: number;
  total_pecas_mes: number;
  total_pecas_periodo: number;
  total_a_receber?: number;
  contagens_abertas: number;
  contagens_finalizadas: number;
  ranking_contagens_dia: DashboardRankingResponsavel[];
  ranking_contagens_mes: DashboardRankingResponsavel[];
  ranking_contagens_periodo: DashboardRankingResponsavel[];
  pecas_ultimos_dias?: DashboardPecasDia[];
  responsaveis_a_receber?: DashboardResponsavelReceber[];
}

export interface ConferenciaEntradaItem {
  id_item?: number;
  id_produto?: number;
  codigo_produto?: string;
  produto?: string;
  tamanho: string;
  quantidade: number;
}

export interface ConferenciaEntrada {
  id_conferencia: number;
  id_nota?: number;
  numero_nota?: string;
  id_cliente: number;
  cliente: string;
  id_usuario?: number;
  responsavel?: string;
  id_produto?: number;
  codigo_produto?: string;
  produto?: string;
  produtos_resumo?: string;
  faccao: string;
  data_recebimento: string;
  data_conferencia: string;
  referencia?: string;
  cor?: string;
  observacao?: string;
  status: 'ABERTA' | 'CONFERIDA' | 'CANCELADA';
  total_pecas: number;
  itens?: ConferenciaEntradaItem[];
}

export interface Contagem {
  id_contagem: number;
  id_nota?: number;
  numero_nota?: string;
  id_cliente: number;
  cliente: string;
  id_produto?: number;
  codigo_produto?: string;
  produto?: string;
  produtos_resumo?: string;
  id_usuario?: number;
  responsavel?: string;
  responsaveis?: string;
  id_usuarios?: number[];
  usuarios?: Usuario[];
  data_contagem: string;
  referencia?: string;
  descricao?: string;
  cor?: string;
  observacao?: string;
  status: 'ABERTA' | 'FINALIZADA' | 'CANCELADA';
  total_pecas: number;
  itens: ContagemItem[];
  logs?: ContagemLog[];
  anexos?: ContagemAnexo[];
}

export interface NotaTransferenciaItem {
  id_produto?: number;
  codigo_produto?: string;
  produto?: string;
  tamanho?: string;
  quantidade: number;
  valor_unitario?: number | string;
  quantidade_nota?: number;
  total_conferido?: number;
  total_contado?: number;
  total_retirado?: number;
  saldo_disponivel?: number;
}

export interface NotaRetirada {
  id_retirada: number;
  id_nota: number;
  numero_nota?: string;
  id_cliente?: number;
  cliente?: string;
  id_contagem?: number;
  id_contagens?: number[];
  data_retirada: string;
  responsavel?: string;
  observacao?: string;
  criado_em?: string;
  total_pecas?: number;
  itens: NotaTransferenciaItem[];
}

export interface PagamentoRetirada {
  id_retirada: number;
  id_retiradas?: number[];
  id_pagamento?: number;
  numero_pagamento?: string;
  id_nota: number;
  numero_nota: string;
  id_cliente: number;
  cliente: string;
  data_retirada: string;
  responsavel_retirada?: string;
  responsaveis: string[];
  responsaveis_detalhes?: { nome: string; percentual_pagamento: number }[];
  percentuais_responsaveis?: Record<string, number>;
  contagens?: {
    id_contagem: number;
    data_contagem: string;
    responsaveis: string[];
    responsaveis_detalhes?: { nome: string; percentual_pagamento: number }[];
    quantidade: number;
  }[];
  id_produto: number;
  codigo_produto: string;
  produto: string;
  tamanho: string;
  quantidade: number;
  tipo_documento?: string;
  numero_documento?: string;
  valor_unitario: number;
  valor_documento?: number | null;
  ajustes_individuais?: PagamentoAjusteIndividual[];
  data_pagamento?: string;
  status?: 'EM_ANDAMENTO' | 'FINALIZADO' | 'CANCELADO' | null;
  finalizado_por?: number;
  finalizado_por_nome?: string;
  finalizado_em?: string;
  observacao_pagamento?: string;
  total: number;
}

export interface PagamentoAjusteIndividual {
  id_ajuste?: number;
  responsavel: string;
  data_contagem?: string | null;
  tipo: 'DESCONTO' | 'ACRESCIMO';
  valor: number;
  descricao: string;
}

export interface PagamentoPix {
  responsavel: string;
  valor: number;
  txid: string;
  chave_pix: string;
  copia_cola: string;
  qr_code: string;
  pendencia?: string;
  pago?: boolean;
  pago_em?: string | null;
  pago_por_nome?: string | null;
}

export interface CaixaLancamento {
  id_caixa: number;
  tipo: 'ENTRADA' | 'SAIDA';
  descricao: string;
  categoria?: string;
  valor: number;
  data_lancamento: string;
  data_vencimento: string;
  pago: boolean;
  pago_em?: string | null;
  origem_pagamento?: string | null;
  socio_pagador?: string | null;
  tipo_pagamento?: string | null;
  detalhe_pagamento?: string | null;
  observacao?: string;
  criado_por?: number;
  criado_por_nome?: string;
  criado_em?: string;
  atualizado_em?: string;
  origem_tipo?: string;
  origem_chave?: string;
  automatico?: boolean;
}

export interface CaixaResumo {
  lancamentos: CaixaLancamento[];
  total_entradas: number;
  total_saidas: number;
  saldo: number;
}

export interface ContaFinanceira {
  id_conta: number;
  tipo: 'PAGAR' | 'RECEBER';
  descricao: string;
  pessoa?: string | null;
  id_fornecedor?: number | null;
  fornecedor_nome?: string | null;
  id_usuario?: number | null;
  usuario_nome?: string | null;
  categoria?: string | null;
  valor: number;
  data_emissao: string;
  data_vencimento: string;
  status: 'ABERTA' | 'VENCIDA' | 'QUITADA' | 'CANCELADA';
  data_baixa?: string | null;
  tipo_pagamento?: string | null;
  detalhe_pagamento?: string | null;
  observacao?: string | null;
  criado_por?: number | null;
  criado_por_nome?: string | null;
  criado_em?: string;
  atualizado_em?: string | null;
}

export interface ContaPix {
  id_conta: number;
  responsavel: string;
  valor: number;
  chave_pix: string;
  copia_cola: string;
  qr_code: string;
}

export interface ContasFinanceirasResumo {
  contas: ContaFinanceira[];
  total_aberto: number;
  total_vencido: number;
  total_quitado: number;
  total_cancelado: number;
}

export interface OcorrenciaRetirada {
  id_ocorrencia: number;
  id_nota: number;
  numero_nota: string;
  id_cliente: number;
  cliente: string;
  id_retirada?: number | null;
  origem?: 'RETIRADA' | 'FINALIZACAO_ORDEM' | string;
  id_produto: number;
  codigo_produto: string;
  produto: string;
  tamanho: string;
  quantidade_retirada: number;
  saldo_conferido: number;
  quantidade_divergente: number;
  status: 'ABERTA' | 'RESOLVIDA' | 'CANCELADA';
  observacao?: string;
  resolvido_por?: number;
  resolvido_por_nome?: string;
  resolvido_em?: string;
  criado_em?: string;
  atualizado_em?: string;
}

export interface OcorrenciaConferenciaLinha {
  id_conferencia: number;
  data_conferencia: string;
  faccao?: string;
  status: string;
  id_item: number;
  quantidade: number;
}

export interface OcorrenciaContagemLinha {
  id_contagem: number;
  data_contagem: string;
  responsaveis?: string;
  status: string;
  id_item: number;
  quantidade: number;
  defeitos: number;
  total: number;
}

export interface OcorrenciaResolucao {
  ocorrencia: OcorrenciaRetirada;
  conferencias: OcorrenciaConferenciaLinha[];
  contagens: OcorrenciaContagemLinha[];
}

export interface NotaTransferencia {
  id_nota: number;
  id_cliente: number;
  cliente: string;
  id_produto?: number;
  codigo_produto: string;
  produto: string;
  produtos_resumo?: string;
  numero_nota: string;
  data_nota: string;
  data_recebimento: string;
  faccao?: string;
  cor?: string;
  quantidade_nota: number;
  observacao?: string;
  observacao_divergencia?: string;
  status: 'ABERTA' | 'EM_CONTAGEM' | 'PARCIALMENTE_RETIRADA' | 'CONFERIDA' | 'DIVERGENTE' | 'FINALIZADA' | 'CANCELADA';
  total_conferido: number;
  total_contado: number;
  total_retirado: number;
  diferenca_nota: number;
  saldo_disponivel: number;
  criado_em?: string;
  atualizado_em?: string;
  itens?: NotaTransferenciaItem[];
  saldos?: NotaTransferenciaItem[];
  conferencias?: ConferenciaEntrada[];
  contagens?: Contagem[];
  retiradas?: NotaRetirada[];
}

export const TAMANHOS = [
  'PP', 'P', 'M', 'G', 'GG', 'XG', 'EXG', 'XGG', 'XXG', 'XXXG',
  'G1', 'G2', 'G3', 'G4', 'G5',
  '1', '2', '3', '4', '6', '8', '10', '12', '14', '16'
];
